import React from 'react';

import Dashboard from './pages/Dashboard';
import Database from './pages/Database';
import Bindings from './pages/Bindings';
import Reports from './pages/Reports';
import Sla from './pages/Sla';
import Overdue from './pages/Overdue';
import Stars from './pages/Stars';
import Support from './pages/Support';
import Settings from './pages/Settings';

function getTg() {
  try {
    return window.Telegram?.WebApp || null;
  } catch (_) {
    return null;
  }
}

async function apiFetch(path, initData, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (initData) headers['x-telegram-init-data'] = initData;

  const res = await fetch(path, {
    method: options.method || 'GET',
    headers,
    body: options.body,
    credentials: 'same-origin'
  });

  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    data = null;
  }

  return { ok: res.ok, status: res.status, data };
}

function safeUsername(user) {
  if (!user) return '';
  const u = user.username ? `@${user.username}` : '';
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return u || name || '';
}

const TABS = [
  { key: 'dashboard', label: '📊', title: 'Дашборд', component: Dashboard },
  { key: 'database', label: '🗄️', title: 'База', component: Database },
  { key: 'settings', label: '⚙️', title: 'Настройки', component: Settings },
  { key: 'sla', label: '⏱️', title: 'SLA', component: Sla },
  { key: 'overdue', label: '🚨', title: 'Просрочки', component: Overdue },
  { key: 'bindings', label: '🔗', title: 'Привязки', component: Bindings },
  { key: 'stars', label: '⭐', title: 'Звёзды', component: Stars },
  { key: 'support', label: '👨‍🔧', title: 'Поддержка', component: Support },
  { key: 'reports', label: '🧾', title: 'Отчёты', component: Reports }
];

// Простая интеграция для страниц: initData + api клиент
export const AppContext = React.createContext({
  tg: null,
  initData: '',
  api: {
    get: async () => ({ ok: false, status: 0, data: null }),
    post: async () => ({ ok: false, status: 0, data: null }),
    del: async () => ({ ok: false, status: 0, data: null })
  }
});

export function useApp() {
  return React.useContext(AppContext);
}

export default function App() {
  const tg = React.useMemo(() => getTg(), []);

  const initData = React.useMemo(() => {
    try {
      return tg?.initData || '';
    } catch (_) {
      return '';
    }
  }, [tg]);

  const api = React.useMemo(() => {
    return {
      get: (path) => apiFetch(path, initData, { method: 'GET' }),
      post: (path, body, headers = {}) =>
        apiFetch(path, initData, {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...headers },
          body: body == null ? undefined : JSON.stringify(body)
        }),
      del: (path) => apiFetch(path, initData, { method: 'DELETE' })
    };
  }, [initData]);

  const [me, setMe] = React.useState(null);
  const [meLoading, setMeLoading] = React.useState(true);
  const [meError, setMeError] = React.useState('');

  const [tab, setTab] = React.useState(() => {
    const h = (window.location.hash || '').replace('#', '').trim();
    return TABS.some((t) => t.key === h) ? h : 'dashboard';
  });

  React.useEffect(() => {
    // Telegram Mini App UX
    try {
      tg?.ready?.();
      tg?.expand?.();
    } catch (_) {}
  }, [tg]);

  React.useEffect(() => {
    // Expose initData for legacy pages (back-compat)
    try {
      window.__TG_INIT_DATA__ = initData;
    } catch (_) {}

    // If opened outside Telegram, show a clear message
    if (!tg) {
      setMeLoading(false);
      setMeError('Откройте админку из Telegram (Mini App).');
      return;
    }

    let cancelled = false;

    (async () => {
      setMeLoading(true);
      setMeError('');

      const r = await api.get('/api/me');
      if (cancelled) return;

      if (!r.ok) {
        const details = r?.data?.details || r?.data?.error || `HTTP ${r.status}`;
        setMeError(`Нет доступа: ${details}`);
        setMe(null);
        setMeLoading(false);
        return;
      }

      const payload = r.data || null;
      setMe(payload);
      setMeLoading(false);

      try {
        window.__ME__ = payload;
      } catch (_) {}
    })().catch((e) => {
      if (cancelled) return;
      setMeError(String(e?.message || e || 'Ошибка загрузки профиля'));
      setMe(null);
      setMeLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [tg, initData, api]);

  const isAdmin = !!me?.isAdmin;
  const userLabel = safeUsername(me?.user);

  const availableTabs = React.useMemo(() => {
    if (!me || !isAdmin) {
      // Non-admin: показываем только дашборд
      return TABS.filter((t) => t.key === 'dashboard');
    }
    return TABS;
  }, [me, isAdmin]);

  React.useEffect(() => {
    // Синхронизируем с hash, чтобы можно было делиться ссылкой внутри mini-app
    try {
      window.location.hash = tab;
    } catch (_) {}
  }, [tab]);

  React.useEffect(() => {
    // И обратная синхронизация: если пользователь открыл ссылку с #tab
    const onHash = () => {
      const h = (window.location.hash || '').replace('#', '').trim();
      if (h && TABS.some((t) => t.key === h)) setTab(h);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  React.useEffect(() => {
    if (!availableTabs.some((t) => t.key === tab)) {
      setTab(availableTabs[0]?.key || 'dashboard');
    }
  }, [availableTabs, tab]);

  const current = availableTabs.find((t) => t.key === tab) || availableTabs[0] || TABS[0];
  const Page = current.component;

  return (
    <AppContext.Provider value={{ tg, initData, api }}>
      <div style={styles.app}>
        <div style={styles.topbar}>
          <div style={styles.topRow}>
            <div style={styles.brand}>PB Star Router</div>
            <div style={styles.me}>
              {meLoading ? 'Проверка доступа…' : meError ? '⛔ Нет доступа' : isAdmin ? '✅ Админ' : 'ℹ️ Просмотр'}
              {userLabel ? ` · ${userLabel}` : ''}
            </div>
          </div>
          <div style={styles.title}>{current.title}</div>
        </div>

        <div style={styles.content}>
          {meLoading ? (
            <div style={styles.card}>Загрузка…</div>
          ) : meError ? (
            <div style={styles.card}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>Доступ ограничен</div>
              <div style={{ opacity: 0.8 }}>{meError}</div>
            </div>
          ) : (
            <Page />
          )}
        </div>

        <div style={styles.nav}>
          {availableTabs.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  ...styles.navBtn,
                  ...(active ? styles.navBtnActive : null)
                }}
                title={t.title}
              >
                <div style={styles.navIcon}>{t.label}</div>
                <div style={{ ...styles.navText, ...(active ? styles.navTextActive : null) }}>{t.title}</div>
              </button>
            );
          })}
        </div>
      </div>
    </AppContext.Provider>
  );
}

const styles = {
  app: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f6f7fb'
  },
  topbar: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    padding: '10px 12px',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    background: '#fff'
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10
  },
  me: {
    fontSize: 12,
    opacity: 0.75,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 240
  },
  brand: {
    fontSize: 12,
    opacity: 0.7,
    marginBottom: 4
  },
  title: {
    fontSize: 16,
    fontWeight: 800
  },
  content: {
    flex: 1,
    paddingBottom: 78 // место под нижнее меню
  },
  card: {
    maxWidth: 720,
    margin: '14px auto 0',
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 12,
    padding: 14
  },
  nav: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(72px, 1fr))',
    gap: 6,
    padding: '8px 8px 10px',
    background: '#fff',
    borderTop: '1px solid rgba(0,0,0,0.08)'
  },
  navBtn: {
    border: '1px solid rgba(0,0,0,0.08)',
    background: 'rgba(0,0,0,0.02)',
    borderRadius: 12,
    padding: '8px 6px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  navBtnActive: {
    border: '1px solid rgba(41,128,185,0.35)',
    background: 'rgba(41,128,185,0.08)'
  },
  navIcon: {
    fontSize: 16,
    lineHeight: '16px'
  },
  navText: {
    marginTop: 4,
    fontSize: 10,
    opacity: 0.7
  },
  navTextActive: {
    opacity: 1,
    fontWeight: 800
  }
};
